import { ShipToIdentification } from '@next/core-lib/business-partner-aggregation-api';
import { SessionSelectors } from '@next/core-lib/session';
import { Identification, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  CropAid,
  Package,
  OrderplanPeriodDisplayData,
  OrderplanRequisiteDisplaySummary,
  OrderplanCropAidDisplayData,
  SalesOrderPeriod,
  ManuallyAddedCropAidDisplay,
  OrderLineStatus,
  DeliveryPeriodMomentDisplay,
  OrderReport,
  RapportOrderLine,
  PreferredDeliveryPeriods,
  PeriodSystem,
  OrderLine,
  ProductInventoryData,
} from '../overview/models';
import { BemProduct, OrderPlanOverviewV2 } from '../overview-v2/models';
import { AppState, ArableSessionState } from '../shared/models';

export const selectApp = createFeatureSelector<AppState>('app');

export const selectUserSession = createFeatureSelector<{
  user?: { customerId?: number; locationId?: number };
}>('session');

export const selectSessionCustomerId = createSelector(
  selectUserSession,
  (session) => session?.user?.customerId,
);

export const selectSessionLocationId = createSelector(
  selectUserSession,
  (session) => session?.user?.locationId,
);

export const selectLoggedInEmployee = createSelector(selectApp, (app) => app.loggedInEmployee);

export const selectLoggedInEmployeeAsArray = createSelector(selectApp, (app) =>
  app.loggedInEmployee ? [...[app.loggedInEmployee]] : [],
);

export const selectOrderPlanByPeriods = createSelector(
  selectApp,
  (app: AppState) => app.orderPlanByPeriods || [],
);

export const selectPreferredDeliveryPeriods = createSelector(
  selectApp,
  (app: AppState) => app.preferredDeliveryPeriods || null,
);

export const selectPeriodSystem = createSelector(selectApp, (app: AppState) =>
  app.periodSystem ? app.periodSystem : PeriodSystem.GBM,
);

export const selectOrderPlanByCropAids = createSelector(
  selectApp,
  (app: AppState) => app.orderPlanByCropAids || [],
);

export const selectGbmOrderLines = createSelector(selectApp, (app: AppState) =>
  app.gbmOrderLines || [],
);

export const selectCropAids = createSelector(selectApp, (app: AppState) =>
  (app.cropAids || []).concat(app.proprietaryCropAids || []),
);

export const selectPackages = createSelector(selectApp, (app: AppState) => app.packages || []);

export const selectOpenGbmOrderLines = createSelector(selectGbmOrderLines, (orderLines) =>
  orderLines.filter((orderLine) => orderLine.status === OrderLineStatus.open),
);

export const selectNotOpenGBMOrderLines = createSelector(selectGbmOrderLines, (orderLines) =>
  orderLines.filter((orderLine) => orderLine.status !== OrderLineStatus.open),
);

export const selectSalesOrderPeriods = createSelector(selectApp, (app: AppState) => {
  if (app.salesOrderPeriods) {
    return app.salesOrderPeriods
      .slice()
      .filter(
        (salesOrderPeriod) =>
          salesOrderPeriod.periodType === (app.periodSystem ? app.periodSystem : PeriodSystem.GBM),
      )
      .sort((periodA, periodB) =>
        !(periodA?.periodNumber && periodB?.periodNumber)
          ? 0
          : periodA.periodNumber > periodB.periodNumber
            ? 1
            : -1,
      );
  }
  return [];
});

export const selectOrderedId = createSelector(selectApp, (app: AppState) => app.orderedId);

export const selectCurrentDate = createSelector(selectApp, () => new Date());

export const selectCodeString = (codes: string[]) => createSelector(selectApp, (_: AppState ) => {
  if (!codes?.length) {
    return;
  }

  return `(${codes.join(', ')})`;
});

export const selectActiveCropAids = createSelector(selectCropAids, (cropAids: CropAid[]) =>
  cropAids ? cropAids.filter((p) => p.isActive) : [],
);

export const selectPackageById = (packageId: number) => createSelector(
  selectPackages,
  (packages: Package[] ) => packages.find((p) => p.id === packageId),
);

export const selectLocationActiveSprayLicenses = createSelector(
  UserSelectors.selectLocations,
  selectCurrentDate,
  (locations: SelectedLocation[], currentDate) => {
    const map: { [locationId: number]: Identification[] } = {};
    locations.forEach((location) => {
      map[location.id] = [];
      map[location.id].push(
        ...location.identifications.filter(
          (identification) =>
            identification.name === ShipToIdentification.ZCPNL1 &&
            isActiveSprayLicense(identification, currentDate),
        ),
      );
    });
    return map;
  },
);

export const selectSprayLicenses = createSelector(
  UserSelectors.selectLocations,
  (locations: SelectedLocation[]) => {
    const sprayLicenses: Identification[] = [];

    for (const location of locations) {
      sprayLicenses.push(
        ...location.identifications.filter(
          (identification) => identification.name === ShipToIdentification.ZCPNL1,
        ),
      );
    }

    return sprayLicenses;
  },
);

export const selectActiveSprayLicenses = createSelector(
  selectSprayLicenses,
  selectCurrentDate,
  (sprayLicenses, currentDate) =>
    sprayLicenses.filter((license) => isActiveSprayLicense(license, currentDate)),
);

export const isAnySprayLicenseActive = createSelector(
  selectSprayLicenses,
  selectCurrentDate,
  (sprayLicenses: Identification[], currentDate: Date) =>
    sprayLicenses.filter((license) => isActiveSprayLicense(license, currentDate)).length > 0,
);

export const selectArableSession = createSelector(
  SessionSelectors.selectSessionState,
  (session: ArableSessionState) => session?.arable,
);

export const selectDefaultYear = createSelector(selectApp, (store) => store.defaultYear);

export const getYear = createSelector(selectArableSession, selectDefaultYear, (arable, defaultYear) => arable?.cropYear ? arable.cropYear : defaultYear,
);

export const isDefaultYear = createSelector(
  getYear,
  selectDefaultYear,
  (year, defaultYear) => year === defaultYear,
);

export const getAvailableYears = createSelector(selectApp, (store) => store.availableYears || []);

export const selectActiveSalesOrderPeriods = createSelector(
  selectSalesOrderPeriods,
  selectCurrentDate,
  (salesOrderPeriods, currentDate) =>
    salesOrderPeriods.filter(
      (salesOrderPeriod) => !isPeriodExpiredHelper(salesOrderPeriod, currentDate),
    ),
);

export const selectOrderplanByPeriodsDisplay = createSelector(
  selectOrderPlanByPeriods,
  selectGbmOrderLines,
  selectSalesOrderPeriods,
  selectCropAids,
  selectPackages,
  (orderplanByPeriods, orderLines, salesOrderPeriods, cropAids, packages) => {
    const orderplanByPeriodsDisplay: OrderplanPeriodDisplayData[] = [];

    // Validate if all ingredients are available
    if (
      !salesOrderPeriods?.length ||
      !cropAids ||
      !cropAids.length ||
      !packages ||
      !packages.length
    ) {
      return orderplanByPeriodsDisplay;
    }

    // Create orderplan
    orderplanByPeriods.forEach((orderplanByPeriod) => {
      const displayData: OrderplanPeriodDisplayData = {
        period: salesOrderPeriods.find(
          (salesOrderPeriod) => salesOrderPeriod.id === orderplanByPeriod.periodId,
        ),
        orderplanRequisiteSummaries: orderplanByPeriod.orderplanRequisiteSummaries.map(
          (requisiteSummary): OrderplanRequisiteDisplaySummary => ({
            ...requisiteSummary,
            cropAid: cropAids.find(({ id }) => id === requisiteSummary.cropAidId),
            orderLines: orderLines
              .filter(
                (orderLine) =>
                  orderLine.periodId === orderplanByPeriod.periodId &&
                  orderLine.cropAidId === requisiteSummary.cropAidId,
              )
              .sort((a, b) => sortOnOrderLineStatus(a, b)),
          }),
        ),
        manuallyAddedCropAids: orderplanByPeriod.manuallyAddedProducts.map(
          (manuallyAddedCropAid): ManuallyAddedCropAidDisplay => ({
            cropAid: cropAids.find(({ id }) => id === manuallyAddedCropAid.cropAidId),
            orderLinesTotalAmount: manuallyAddedCropAid.orderLinesTotalAmount,
            orderLines: orderLines
              .filter(
                (orderLine) =>
                  orderLine.periodId === orderplanByPeriod.periodId &&
                  orderLine.cropAidId === manuallyAddedCropAid.cropAidId,
              )
              .sort((a, b) => sortOnOrderLineStatus(a, b)),
          }),
        ),
      };

      orderplanByPeriodsDisplay.push(displayData);
    });

    // Sorting
    orderplanByPeriodsDisplay.forEach((period) => {
      period.orderplanRequisiteSummaries.sort((a, b) =>
        !(a.cropAid?.name && b.cropAid?.name) ||
        a.cropAid.name.toLowerCase() > b.cropAid.name.toLowerCase()
          ? 1
          : -1,
      );
      period.manuallyAddedCropAids.sort((a, b) =>
        !(a.cropAid?.name && b.cropAid?.name) ||
        a.cropAid.name.toLowerCase() > b.cropAid.name.toLowerCase()
          ? 1
          : -1,
      );
    });

    return orderplanByPeriodsDisplay;
  },
);
export const selectOrderplanV2Display = createSelector(
  selectApp,
  (app: AppState) => app.orderPlanV2 || [],
);

export const selectOpenOrderplanByPeriodsDisplay = createSelector(
  selectOrderplanByPeriodsDisplay,
  (orderplanByPeriods) =>
    orderplanByPeriods
      .map(
        (orderplanByPeriod): OrderplanPeriodDisplayData => ({
          period: orderplanByPeriod.period,
          manuallyAddedCropAids: orderplanByPeriod.manuallyAddedCropAids.map(
            (manuallyAddedCropAid) => {
              const openOrderLines = manuallyAddedCropAid.orderLines.filter(
                (orderLine) => orderLine.status === OrderLineStatus.open,
              );

              return {
                ...manuallyAddedCropAid,
                orderLines: openOrderLines,
              };
            },
          ),
          orderplanRequisiteSummaries: orderplanByPeriod.orderplanRequisiteSummaries.map(
            (orderplanRequisiteSummary) => {
              const openOrderLines = orderplanRequisiteSummary.orderLines.filter(
                (orderLine) => orderLine.status === OrderLineStatus.open,
              );
              return {
                ...orderplanRequisiteSummary,
                orderLines: openOrderLines,
              };
            },
          ),
        }),
      )
      .filter(
        (orderplanByPeriod) =>
          orderplanByPeriod.manuallyAddedCropAids.find(
            (manuallyAddedCropAid) => manuallyAddedCropAid.orderLines.length > 0,
          ) != null ||
          orderplanByPeriod.orderplanRequisiteSummaries.find(
            (orderplanRequisiteSummary) => orderplanRequisiteSummary.orderLines.length > 0,
          ) != null,
      ),
);

export const selectOrderplanByCropAidsDisplay = createSelector(
  selectOrderPlanByCropAids,
  selectCropAids,
  (orderplanByCropAids, cropAids) => {
    const orderplanByCropAidsDisplay: OrderplanCropAidDisplayData[] = [];
    if (!cropAids?.length) {
      return orderplanByCropAidsDisplay;
    }

    orderplanByCropAids.forEach((orderplanByCropAid) => {
      const displayData: OrderplanCropAidDisplayData = {
        ...orderplanByCropAid,
        cropAid: cropAids.find((p) => p.id === orderplanByCropAid.cropAidId),
      };

      orderplanByCropAidsDisplay.push(displayData);
    });

    return orderplanByCropAidsDisplay;
  },
);

export const selectOrderPlanLoaded = createSelector(
  selectApp,
  (appState)=>  (appState.orderPlanLoaded),
);

export const selectOrderplanByCropAidsWithExcess = createSelector(
  selectOrderplanByCropAidsDisplay,
  (orderplanByCropAids) =>
    orderplanByCropAids.filter(
      (orderplanByCropAid) =>
        orderplanByCropAid.orderLinesTotalAmount +
          orderplanByCropAid.customerProductInventoryAmount >
        orderplanByCropAid.requisiteAmount,
    ),
);

export const selectSalesOrderPeriodById = (id: number) => createSelector(
  selectSalesOrderPeriods,
  (salesOrderPeriods: SalesOrderPeriod[]) =>
    salesOrderPeriods.find((salesOrderPeriod) => salesOrderPeriod.id === id),
);

export const selectSalesOrderPeriodsWithMaxPeriodById = (id: number) => createSelector(
  selectActiveSalesOrderPeriods,
  selectSalesOrderPeriodById(id),
  selectCurrentDate,
  selectPreferredDeliveryPeriods,
  (salesOrderPeriods, currentPeriod, currentDate, preferedPeriods) =>
    currentPeriod
      ? salesOrderPeriods.filter(
        (salesOrderPeriod) =>
          !isPeriodExpiredHelper(salesOrderPeriod, currentDate) &&
            isPeriodWithinPreferedPeriods(salesOrderPeriod, preferedPeriods) &&
            !!salesOrderPeriod?.periodNumber &&
            !!currentPeriod?.periodNumber &&
            salesOrderPeriod.periodNumber <= currentPeriod.periodNumber,
      )
      : salesOrderPeriods.filter(
        (salesOrderPeriod) =>
          !isPeriodExpiredHelper(salesOrderPeriod, currentDate) &&
            isPeriodWithinPreferedPeriods(salesOrderPeriod, preferedPeriods),
      ),
);

export const selectDisplayPeriods = createSelector(
  selectOpenGbmOrderLines,
  selectSalesOrderPeriods,
  (orderLines, salesOrderPeriods): DeliveryPeriodMomentDisplay[] => {
    if (!salesOrderPeriods?.length) {
      return [];
    }

    const filteredPeriods = salesOrderPeriods
      .filter((sop) => orderLines.findIndex((o) => o.periodId === sop.id) !== -1);

    return filteredPeriods
      .map(
        (period): DeliveryPeriodMomentDisplay => ({
          period,
          deliveryPeriodMoment: orderLines
            .find((orderLine) => orderLine.periodId === period.id).deliveryPeriodMoment,
        }),
      ).sort((a, b) => {
        if (!(a.period?.periodNumber && b.period?.periodNumber)) {
          return 0;
        }

        if (a.period.periodNumber > b.period.periodNumber) {
          return 1;
        }

        if (a.period.periodNumber < b.period.periodNumber) {
          return -1;
        }

        return 0;
      });
  },
);

export const selectOpenOrderplanNotExpiredPeriod = createSelector(
  selectOpenOrderplanByPeriodsDisplay,
  selectCurrentDate,
  (orderplanByPeriods, currentDate) =>
    orderplanByPeriods.filter(
      (orderplanByPeriod) => !isPeriodExpiredHelper(orderplanByPeriod.period, currentDate),
    ),
);

export const selectInactiveCropAidsInOrderplan = createSelector(
  selectOpenOrderplanNotExpiredPeriod,
  (orderplanByPeriods) => {
    const inactiveCropAids: CropAid[] = [];

    orderplanByPeriods.forEach((orderplanByPeriod) => {
      orderplanByPeriod.orderplanRequisiteSummaries.forEach((requisiteSummary) => {
        if (!requisiteSummary.cropAid?.isActive && requisiteSummary.orderLines.length > 0) {
          inactiveCropAids.push(requisiteSummary.cropAid);
        }
      });

      orderplanByPeriod.manuallyAddedCropAids.forEach((manualyAddedProduct) => {
        if (!manualyAddedProduct.cropAid?.isActive && manualyAddedProduct.orderLines.length > 0) {
          inactiveCropAids.push(manualyAddedProduct.cropAid);
        }
      });
    });

    return inactiveCropAids.filter(
      (p, index) => index === inactiveCropAids.findIndex((x) => x && p && x.id === p.id),
    );
  },
);

export const selectInactivePackagesInOrderplan = createSelector(
  selectOpenOrderplanNotExpiredPeriod,
  selectPackages,
  (orderplanByPeriods, packages) => {
    const inactivePackages: Package[] = [];

    orderplanByPeriods.forEach((orderplanByPeriod) => {
      orderplanByPeriod.orderplanRequisiteSummaries.forEach((requisiteSummary) => {
        requisiteSummary.orderLines.forEach((orderLine) => {
          const orderLinePackage = packages.find((p) => p.id === orderLine.packageId);

          if (!orderLinePackage.isActive) {
            inactivePackages.push(orderLinePackage);
          }
        });
      });

      orderplanByPeriod.manuallyAddedCropAids.forEach((manualyAddedCropAid) => {
        manualyAddedCropAid.orderLines.forEach((orderLine) => {
          const orderLinePackage = packages.find((p) => p.id === orderLine.packageId);

          if (!orderLinePackage.isActive) {
            inactivePackages.push(orderLinePackage);
          }
        });
      });
    });

    return inactivePackages.filter(
      (p, index) => index === inactivePackages.findIndex((x) => x.id === p.id),
    );
  },
);

export const selectOrderPlanningReportsByPeriodAndStatus = createSelector(
  selectGbmOrderLines,
  selectSalesOrderPeriods,
  selectPackages,
  selectCropAids,
  selectOrderPlanByPeriods,
  (orderLines, orderPeriods, packages, cropAids, orderPlanByPeriods) => {
    const orderPlanningReports: OrderReport[] = [];

    // Create orderPlanningReports array
    orderLines.forEach((filteredOrderLine) => {
      // Add package and cropaid to orderline
      const orderLine: RapportOrderLine = { ...filteredOrderLine, cropAid: null, package: null };
      orderLine.package = packages.find((p) => p.id === orderLine.packageId);
      orderLine.cropAid = cropAids.find(({ id }) => id === orderLine.cropAidId) ?? null;

      // Find sales order period object for orderline
      const orderPeriod = orderPeriods.find((p) => p.id === orderLine.periodId);

      // Check if report for same period and status exists
      const currentReport = orderPlanningReports.find(
        (r) => r.periodInfo?.id === orderLine.periodId && r.status === orderLine.status,
      );

      // Add orderline to existing report, or create new one
      if (currentReport) {
        currentReport.orderLines.push(orderLine);
      } else {
        orderPlanningReports.push({
          cartId: orderLine.cartId,
          orderedId: orderLine.orderedId,
          checkoutId: orderLine.checkoutId,
          orderedDate: orderLine.orderedDate,
          periodInfo: orderPeriod,
          status: orderLine.status,
          orderLines: [orderLine],
        });
      }
    });

    //Add requisites that can't be ordered (no packages or inventory/overflow)
    orderPlanByPeriods.forEach((orderPlan) => {
      const orderReport: OrderReport = {
        periodInfo: orderPeriods.find((period) => period.id === orderPlan.periodId),
        orderLines: [],
        orderedDate: '',
      };

      orderPlan.orderplanRequisiteSummaries.forEach((orderPlanRequisiteSummary) => {
        if (orderPlanRequisiteSummary.orderLinesTotalAmount === 0) {
          if (
            orderPlanRequisiteSummary.requisiteAmount >
            orderPlanRequisiteSummary.customerProductInventoryUsedAmount +
              orderPlanRequisiteSummary.usedOrderOverflowAmount
          ) {
            const cropAid = cropAids.find((c) => c.id === orderPlanRequisiteSummary.cropAidId);
            let packageCount = 0;
            if (cropAid) {
              packageCount = packages.filter((p) => p.productId === cropAid.productId).length;
            }

            if (packageCount === 0) {
              orderReport.orderLines.push({
                cropAid,
                count: 0,
                requisiteAmount: orderPlanRequisiteSummary.requisiteAmount,
              });
            }
          }
        }
      });

      if (orderReport.orderLines.length > 0) {
        orderPlanningReports.push(orderReport);
      }
    });

    // Sorting of reports on period and status
    orderPlanningReports.sort((a, b) =>
      !(a.periodInfo?.periodNumber && b.periodInfo?.periodNumber)
        ? 0
        : a.periodInfo.periodNumber === b.periodInfo.periodNumber
          ? a.status > b.status
            ? 1
            : -1
          : a.periodInfo.periodNumber - b.periodInfo.periodNumber,
    );

    // Sorting of orderlines based on cropaid
    orderPlanningReports.forEach((orderReport) => {
      orderReport.orderLines.sort((a, b) =>
        !(a.cropAid?.name && b.cropAid?.name) ||
        a.cropAid.name.toLowerCase() > b.cropAid.name.toLowerCase()
          ? 1
          : -1,
      );
    });

    return orderPlanningReports;
  },
);

export const selectOrderPlanningReportsFinalizedByOrderId = createSelector(
  selectGbmOrderLines,
  selectSalesOrderPeriods,
  selectPackages,
  selectCropAids,
  (orderLines, orderPeriods, packages, cropAids) => {
    const orderPlanningReports: OrderReport[] = [];
    // Create orderPlanningReports array
    orderLines.forEach((filteredOrderLine) => {
      if (filteredOrderLine.status !== OrderLineStatus.finalized) {
        return;
      }

      // Add package and cropaid to orderline
      const orderLine: RapportOrderLine = { ...filteredOrderLine, cropAid: null, package: null };
      orderLine.package = packages.find((p) => p.id === orderLine.packageId);
      orderLine.cropAid = cropAids.find(({ id }) => id === orderLine.cropAidId) ?? null;

      // Find sales order period object for orderline
      const orderPeriod = orderPeriods.find((p) => p.id === orderLine.periodId);
      if (!orderPeriod) {
        // Orderperiods normally always exist, but when switching between years, they still belong
        // to the previously selected year while new ones are being loaded. Hence, no orderPeriod can
        // be found, and we cannot process this orderline.
        return;
      }

      // Check if report for same period and status exists
      const currentReport = orderPlanningReports.find(
        (r) =>
          ((r.cartId && r.cartId === orderLine.cartId) ||
            (r.checkoutId && r.checkoutId === orderLine.checkoutId) ||
            (r.orderedId && r.orderedId === orderLine.orderedId)) &&
          r.periodInfo.id === orderLine.periodId,
      );

      // Add orderline to existing report, or create new one
      if (currentReport) {
        currentReport.orderLines.push(orderLine);
      } else {
        orderPlanningReports.push({
          cartId: orderLine.cartId,
          checkoutId: orderLine.checkoutId,
          orderedId: orderLine.orderedId,
          orderedDate: orderLine.orderedDate,
          periodInfo: orderPeriod,
          status: orderLine.status,
          orderLines: [orderLine],
        });
      }
    });

    // Sorting of reports on period and status
    orderPlanningReports.sort((a, b) =>
      !(a.periodInfo?.periodNumber && b.periodInfo?.periodNumber)
        ? 0
        : a.periodInfo.periodNumber === b.periodInfo.periodNumber
          ? a.status > b.status
            ? 1
            : a.status < b.status
              ? -1
              : 0
          : a.periodInfo.periodNumber - b.periodInfo.periodNumber,
    );

    // Sorting of orderlines based on cropaid
    orderPlanningReports.forEach((orderReport) => {
      orderReport.orderLines.sort((a, b) =>
        !(a.cropAid?.name && b.cropAid?.name) ||
        a.cropAid.name.toLowerCase() > b.cropAid.name.toLowerCase()
          ? 1
          : -1,
      );
    });

    return orderPlanningReports;
  },
);

export const selectOrderReports = createSelector(
  selectOrderPlanningReportsFinalizedByOrderId,
  (orderPlanningReports) => {
    const orderReports: OrderReport[] = [];

    orderPlanningReports.forEach((report) => {
      if (report.status === OrderLineStatus.finalized) {
        orderReports.push(report);
      }
    });

    return orderReports;
  },
);

/**
 * Selects the right order report data based on the
 * identifier of the order.
 */
export const selectOrderReport = createSelector(
  selectOrderReports,
  selectOrderedId,
  (orderReports: OrderReport[], orderedId: number) =>
    orderReports.filter(
      (orderReport) =>
        orderedId &&
        (orderReport.orderedId === orderedId ||
          orderReport.checkoutId === orderedId ||
          orderReport.cartId === orderedId),
    ),
);

export const selectCropFields = createSelector(selectApp, (app: AppState) => app.cropFields || []);

export const selectCropFieldLinkType = createSelector(selectApp, (app: AppState) => app.cropFieldLinkType);

export const selectShipTosWithOrders = createSelector(
  selectApp,
  (app: AppState) => app.shipTosWithOrders || [],
);

export const isPeriodExpired = (id: number) => createSelector(
  selectSalesOrderPeriodById(id),
  selectCurrentDate,
  (salesOrderPeriod: SalesOrderPeriod, currentDate: Date) =>
    isPeriodExpiredHelper(salesOrderPeriod, currentDate),
);

export const selectCustomerProgress = createSelector(selectApp, (store) => store.customerProgress);

export const selectOptimizationStatus = createSelector(
  selectApp,
  (store) => store.isOrderPlanOptimized,
);

export const selectCustomerProductInventory = createSelector(
  selectApp,
  (app: AppState) => app.customerProductInventory || [],
);

export const selectCustomerProductInventoryData = createSelector(
  selectOrderplanV2Display,
  selectCustomerProductInventory,
  selectCropAids,
  (orderplan, productInventories, cropAids) =>
    mergeCustomerProductInventoryData(orderplan, productInventories, cropAids),
);

export const isPeriodExpiredHelper = (salesOrderPeriod: SalesOrderPeriod, currentDate: Date) => {
  if (!salesOrderPeriod) {
    return true;
  }

  const orderBefore = new Date(salesOrderPeriod.orderReceivedBefore);

  return currentDate >= orderBefore;
};


const isPeriodWithinPreferedPeriods = (
  salesOrderPeriod: SalesOrderPeriod,
  preferedPeriods: PreferredDeliveryPeriods,
) => {
  if (!preferedPeriods?.periods.length) {
    return true;
  }

  return preferedPeriods.periods.indexOf(salesOrderPeriod.id) !== -1;
};

const isActiveSprayLicense = (sprayLicense: Identification, currentDate: Date): boolean =>
  new Date(sprayLicense.validTo) > currentDate && new Date(sprayLicense.validFrom) <= currentDate;

const sortOnOrderLineStatus = (orderLine1: OrderLine, orderLine2: OrderLine) => {
  const statusOrder = [
    OrderLineStatus.open,
    OrderLineStatus.inCart,
    'Ordered',
    OrderLineStatus.finalized,
  ];
  const orderLine1Index = statusOrder.indexOf(orderLine1.status);
  const orderLine2Index = statusOrder.indexOf(orderLine2.status);
  return orderLine1Index - orderLine2Index;
};

export const selectDigitalProductSubscriptions = createSelector(selectApp, (store) => store.digitalProductSubscriptions);

export const selectBemOrderLines = createSelector(
  selectApp, (app: AppState): BemProduct[] => app.orderedBemProducts || [],
);

const  mapOrderplanV2ForCustomerProductInventory = (orderplanV2: OrderPlanOverviewV2[]): OrderplanCropAidDisplayData[] =>
  orderplanV2
    .filter((orderplan) => orderplan.isMainCropAid || !orderplan.isPackage)
    .map(
      (orderplan) =>
        ({
          cropAid: { id: orderplan.cropAidId, name: orderplan.cropAidName, unitOfMeasurement: orderplan.unitOfMeasurement } as CropAid,
          customerProductInventoryAmount: orderplan.customerProductInventoryAmount,
          orderLinesTotalAmount: orderplan.orderLinesTotalAmount,
          requisiteAmount: orderplan.isMainCropAid
            ? orderplanV2.find((op) => op.cropAidId === orderplan.cropAidId && op.isPackage)?.requisiteAmount
            : orderplan.requisiteAmount,
        } as unknown as OrderplanCropAidDisplayData),
    );

const mergeCustomerProductInventoryData = (
  orderplanV2: OrderPlanOverviewV2[],
  productInventories: ProductInventoryData[],
  cropAids: CropAid[],
): OrderplanCropAidDisplayData[] => {
  const inventoryByCropAidId = new Map(productInventories.map((productInventory) => [productInventory.cropAidId, productInventory.amount]));
  const orderplanData = mapOrderplanV2ForCustomerProductInventory(orderplanV2).map((product) => ({
    ...product,
    customerProductInventoryAmount: inventoryByCropAidId.get(product.cropAid.id) ?? product.customerProductInventoryAmount,
  }));
  const orderplanCropAidIds = new Set(orderplanData.map((product) => product.cropAid.id));
  const stockOnlyData = productInventories
    .filter((productInventory) => !orderplanCropAidIds.has(productInventory.cropAidId))
    .map((productInventory) => {
      const cropAid = cropAids.find(({ id }) => id === productInventory.cropAidId);
      return cropAid
        ? ({
            cropAid,
            customerProductInventoryAmount: productInventory.amount,
            orderLinesTotalAmount: 0,
            requisiteAmount: 0,
          } as OrderplanCropAidDisplayData)
        : undefined;
    })
    .filter((product): product is OrderplanCropAidDisplayData => !!product);

  return [...orderplanData, ...stockOnlyData];
};
